<template>
  <main class="console-layout">
    <section v-if="viewMintId" class="coin-page">
      <div class="coin-page-head">
        <button class="back-btn" type="button" title="Вернуться к игре" @click="closeMintView">‹</button>
        <div class="coin-title">
          <strong>{{ headName }}</strong>
          <span v-if="headTicker" class="coin-ticker">({{ headTicker }})</span>
        </div>
        <div class="coin-head-actions">
          <span v-if="pnlText" class="head-pnl" :class="pnlTone">{{ pnlText }}</span>
          <button
            class="head-target"
            :class="{ active: positionPnl?.hasPosition }"
            :style="selectedTargetStyle"
            type="button"
            :title="positionPnl?.hasPosition ? 'Выйти из позиции' : 'Снайпинг'"
            @click="handleTarget(selectedEntity)"
          >{{ positionPnl?.hasPosition ? '◎' : '🎯' }}</button>
        </div>
      </div>

      <div class="coin-grid">
        <CoinCard
          :entity="selectedEntity"
          :trades="[]"
          :position-pnl="positionPnl"
          @actions="actionsOpen = true"
        />
      </div>
    </section>

    <div v-if="quickTradeEntity" class="quick-trade-backdrop" @click.self="closeQuickTrade">
      <CoinTrade
        class="quick-trade-modal"
        :entity="quickTradeEntity"
        :guard-nonce="quickTradeGuardNonce"
        quick
        @submitted="handleQuickTradeSubmitted"
        @done="handleQuickTradeDone"
        @failed="handleQuickTradeFailed"
        @cancel="closeQuickTrade"
      />
    </div>

    <div v-if="actionsOpen && selectedEntity" class="actions-backdrop" @click.self="actionsOpen = false">
      <CoinActions
        class="actions-modal"
        :entity="selectedEntity"
        :is-local="isLocalSelectedCoin"
        :is-launched="isLaunched"
        :dev-wallet="devWalletForSelected"
        :dev-wallet-options="wallets"
        :stats="stats"
        :launch-cfg="launchCfg"
        :guard-nonce="tradeGuardNonce"
        @cfg="handleLaunchCfg"
        @launch="handleActionLaunch"
        @copy="openCopyFromCoin"
        @delete="handleActionDelete"
        @own="handleActionOwn"
      />
    </div>

    <div class="toast-stack" aria-live="polite">
      <div v-for="note in notices" :key="note.id" class="toast" :class="note.kind">
        <strong>{{ note.title }}</strong>
        <span>{{ note.text }}</span>
      </div>
    </div>

    <CoinFormModal
      :visible="coinModalVisible"
      mode="copy"
      :initial="coinModalInitial"
      :wallets="wallets"
      :busy="coinModalBusy"
      :error="coinModalError"
      @submit="handleCoinModalSubmit"
      @cancel="closeCoinModal"
    />
  </main>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import CoinFormModal from '@/components/coin/form/modal.vue'
import CoinActions from '@/components/coin/actions/panel.vue'
import CoinTrade from '@/components/coin/actions/trade.vue'
import CoinCard from '@/components/coin/pad/panel.vue'
import { formatPnlWithPct, shortKey } from '@/components/_shared/format'

defineOptions({ name: 'ConsoleWorkspaceView' })

const store = useStorage()
const { pad } = storeToRefs(store)
const tradeGuardNonce = ref(0)
const quickTradeEntity = ref(null)
const quickTradeGuardNonce = ref(0)
const optimisticTargets = ref(new Set())
const optimisticTimers = new Map()
const notices = ref([])
const actionsOpen = ref(false)
const nowTick = ref(Date.now())
let noticeId = 0
const previousPositions = new Map()
const progressTimer = window.setInterval(() => { nowTick.value = Date.now() }, 1000)

onBeforeUnmount(() => {
  window.clearInterval(progressTimer)
  for (const timer of optimisticTimers.values()) window.clearTimeout(timer)
  optimisticTimers.clear()
})

const viewMintId = computed(() => pad.value?.mint || null)
const wallets = computed(() => Object.values(store.wallets || {}))
const mintList = computed(() => Object.entries(store.mints || {}).map(([mint, value]) => ({ ...value, mint })))
const coinList = computed(() => Object.entries(store.coins || {}).map(([mint, value]) => ({ ...value, mint })))
const activePositionCount = computed(openPositionCount)

function openPositionCount() {
  const all = [...mintList.value, ...coinList.value]
  if (pad.value?.mint) all.push({ ...(selectedEntity.value || {}), mint: pad.value.mint, position: pad.value.position || selectedEntity.value?.position })
  const seen = new Set()
  let count = 0
  for (const coin of all) {
    if (!coin?.mint || seen.has(coin.mint)) continue
    seen.add(coin.mint)
    if (hasOpenPosition(coin?.position)) count++
  }
  return count
}

const selectedEntity = computed(() => {
  const mint = viewMintId.value
  if (!mint) return null
  return mintList.value.find(row => row.mint === mint) || coinList.value.find(row => row.mint === mint) || null
})

const positionPnl = computed(() => {
  const entity = selectedEntity.value
  if (!entity) return null
  const currentAmount = Number(entity.amount || 0)
  const currentLamports = Number(entity.lamports || 0)
  const currentPrice = currentAmount > 0 && currentLamports > 0 ? currentLamports / currentAmount : 0
  const position = pad.value?.position || entity.position || null
  let tokenAmount = Number(position?.token_amount || 0)
  const spentLamports = Number(position?.spent_lamports || 0)
  const receivedLamports = Number(position?.received_lamports || 0)
  const finalPosition = isFinalPosition(position)
  if (finalPosition) tokenAmount = 0
  const openValueLamports = finalPosition ? 0 : Number(position?.open_lamports ?? (currentPrice > 0 ? tokenAmount * currentPrice : 0))
  const fallbackTotal = receivedLamports + openValueLamports - spentLamports
  const realizedLamports = Number(position?.realized_lamports ?? (tokenAmount > 0 ? 0 : receivedLamports - spentLamports))
  const totalLamports = Number(position?.total_lamports ?? fallbackTotal)
  const unrealizedLamports = finalPosition ? 0 : Number(position?.unrealized_lamports ?? (totalLamports - realizedLamports))
  return {
    hasPosition: tokenAmount > 0 && !finalPosition,
    tokenAmount,
    spentLamports,
    receivedLamports,
    openValueLamports,
    realizedLamports,
    unrealizedLamports,
    totalLamports,
    stage: String(position?.stage || '').trim(),
    triggeredSteps: Array.isArray(position?.triggered_steps) ? position.triggered_steps : [],
    steps: Array.isArray(position?.steps) ? position.steps : [],
  }
})

const stats = computed(() => positionPnl.value ? { profit: Number(positionPnl.value.realizedLamports || 0), unrealized: Number(positionPnl.value.unrealizedLamports || 0) } : null)
const pnlTone = computed(() => Number(positionPnl.value?.totalLamports || 0) > 0 ? 'pos' : Number(positionPnl.value?.totalLamports || 0) < 0 ? 'neg' : 'flat')
const pnlText = computed(() => {
  if (!positionPnl.value?.hasPosition && Number(positionPnl.value?.totalLamports || 0) === 0) return ''
  const value = Number(positionPnl.value?.totalLamports || 0)
  return Number.isFinite(value) && value !== 0 ? formatPnlWithPct(value, positionPnl.value?.spentLamports) : '0'
})
const headName = computed(() => selectedEntity.value?.name || selectedEntity.value?.symbol || shortKey(viewMintId.value || ''))
const headTicker = computed(() => {
  const symbol = String(selectedEntity.value?.symbol || '').trim()
  const name = String(headName.value || '').trim()
  return symbol && symbol !== name ? symbol : ''
})
const isLocalSelectedCoin = computed(() => {
  const entity = selectedEntity.value
  if (entity?.is_own != null || entity?.isOwn != null) return !!(entity?.is_own ?? entity?.isOwn)
  const owner = String(entity?.owner || '').trim()
  if (!owner) return false
  if (owner === String(store.user?.public_key || '').trim()) return true
  return wallets.value.some(wallet => String(wallet?.public_key || wallet?.publicKey || '').trim() === owner)
})
const isLaunched = computed(() => Number(selectedEntity.value?.trades || 0) > 0 || Number(selectedEntity.value?.holders_count || selectedEntity.value?.holdersCount || 0) > 0)
const devWalletForSelected = computed(() => selectedEntity.value?.owner || '')
const launchCfg = computed(() => store.launch)

const coinModalVisible = ref(false)
const coinModalInitial = ref(null)
const coinModalBusy = ref(false)
const coinModalError = ref('')

function openCopyFromCoin(entity) {
  if (!entity) return
  coinModalInitial.value = {
    mint: entity.mint,
    name: entity.name,
    symbol: entity.symbol,
    description: entity.description,
    website: entity.website,
    twitter: entity.twitter,
    image: entity.image,
  }
  coinModalError.value = ''
  coinModalVisible.value = true
}
function closeCoinModal() { coinModalVisible.value = false }
function handleLaunchCfg(patch) { store.updateLaunch(patch).catch(err => console.error('Launch cfg update failed:', err)) }

function closeMintView() {
  actionsOpen.value = false
  const root = store.state?.value ?? store.state
  if (root && typeof root === 'object') root.pad = {}
  store.view('').catch(err => console.error('view clear failed:', err))
  window.dispatchEvent(new CustomEvent('aime-console-section', { detail: { section: 'home' } }))
}

async function openGuard(coin) {
  const mint = String(coin?.mint || coin || '').trim()
  if (!mint) return
  quickTradeEntity.value = findEntity(mint) || (typeof coin === 'object' ? coin : { mint })
  quickTradeGuardNonce.value += 1
}

async function handleTarget(coin) {
  if (!coin?.mint) return
  const position = store.pad?.mint === coin.mint ? store.pad?.position : coin?.position
  if (hasOpenPosition(position)) {
    await quickSell(coin)
    return
  }
  if (store.autotrade?.singlePosition !== false && activePositionCount.value > 0) {
    pushNotice('bad', 'Position exists', 'One position mode')
    return
  }
  await openGuard(coin)
}

function findEntity(mint) { return mintList.value.find(row => row.mint === mint) || coinList.value.find(row => row.mint === mint) || null }
function positionStage(position) { return String(position?.stage || position?.Stage || '').trim().toUpperCase() }
function isFinalPosition(position) { return positionStage(position) === 'FINAL' }
function hasOpenPosition(position) { return !isFinalPosition(position) && Number(position?.token_amount || 0) > 0 }
function isClosedPosition(position) {
  if (!position) return false
  if (isFinalPosition(position)) return true
  if (hasOpenPosition(position)) return false
  return Number(position.spent_lamports || 0) > 0 || Number(position.received_lamports || 0) > 0
}
function closeQuickTrade() { quickTradeEntity.value = null }
function handleQuickTradeSubmitted(event) {
  const mint = String(event?.mint || quickTradeEntity.value?.mint || '').trim()
  if (mint) schedulePendingClear(mint)
  closeQuickTrade()
}
function handleQuickTradeDone(event) { handleTradeDone(event) }
function handleQuickTradeFailed(event) {
  const mint = String(event?.mint || '').trim()
  if (mint) clearPending(mint)
  pushNotice('bad', event?.kind === 'guarded' ? 'Buy ★ failed' : 'Trade failed', event?.error || shortKey(mint))
}
function handleTradeDone(event) {
  const kind = event?.kind === 'guarded' ? 'Buy ★ sent' : `${event?.kind || 'Trade'} sent`
  pushNotice(event?.kind === 'sell' ? 'exit' : 'good', kind, shortKey(event?.mint || viewMintId.value))
}
function schedulePendingClear(mint) {
  const next = new Set(optimisticTargets.value)
  next.add(mint)
  optimisticTargets.value = next
  if (optimisticTimers.has(mint)) window.clearTimeout(optimisticTimers.get(mint))
  optimisticTimers.set(mint, window.setTimeout(() => { optimisticTimers.delete(mint); clearPending(mint) }, 45000))
}
function clearPending(mint) {
  const key = String(mint || '').trim()
  if (!key) return
  if (optimisticTimers.has(key)) { window.clearTimeout(optimisticTimers.get(key)); optimisticTimers.delete(key) }
  const next = new Set(optimisticTargets.value)
  next.delete(key)
  optimisticTargets.value = next
}

const selectedTargetStyle = computed(() => {
  if (!positionPnl.value?.hasPosition) return {}
  const progress = Math.max(0, Math.min(100, Number(targetProgress(selectedEntity.value) || 0)))
  return { '--target-progress': `${progress * 3.6}deg` }
})
function targetProgress(coin) {
  if (!coin) return 0
  const ttl = Number(store.autotrade?.maxHoldMs || 0)
  if (!Number.isFinite(ttl) || ttl <= 0) return 100
  const opened = Date.parse(String(coin?.position?.opened_at || coin?.created_at || ''))
  if (!Number.isFinite(opened)) return 0
  return ((nowTick.value - opened) / ttl) * 100
}

async function quickSell(coin) {
  const mint = String(coin?.mint || '').trim()
  if (!mint) return
  const owners = {}
  const holders = coin?.holders && typeof coin.holders === 'object' ? coin.holders : {}
  for (const wallet of wallets.value) {
    const pk = String(wallet?.public_key || '').trim()
    if (pk && Number(holders[pk] || 0) > 0) owners[pk] = 100
  }
  if (Object.keys(owners).length === 0) {
    for (const pk of Object.keys(store.trade?.owners || {})) owners[pk] = 100
  }
  if (Object.keys(owners).length === 0) return
  try {
    await store.createTradeBatch(mint, 'sell', owners)
    pushNotice('exit', 'Sell sent', shortKey(mint))
  } catch (error) {
    pushNotice('bad', 'Sell failed', error?.message || shortKey(mint))
  }
}

function pushNotice(kind, title, text) {
  const id = ++noticeId
  notices.value = [{ id, kind, title, text }, ...notices.value].slice(0, 4)
  window.setTimeout(() => { notices.value = notices.value.filter(note => note.id !== id) }, 5200)
}

watch(
  () => [store.pad?.mint, store.pad?.position?.token_amount, positionPnl.value?.totalLamports],
  ([mint, tokenAmount, pnl]) => {
    if (!mint) return
    const amount = hasOpenPosition(store.pad?.position) ? Number(tokenAmount || 0) : 0
    const previous = previousPositions.get(mint)
    previousPositions.set(mint, { amount, pnl: Number(pnl || 0) })
    if (!previous || previous.amount <= 0 || amount > 0) return
    const kind = Number(previous.pnl || 0) >= 0 ? 'good' : 'bad'
    pushNotice(kind, kind === 'good' ? 'Position closed +' : 'Position closed -', shortKey(mint))
  },
)

watch(
  () => [...mintList.value, ...coinList.value]
    .filter(coin => hasOpenPosition(coin?.position) || isClosedPosition(coin?.position))
    .map(coin => `${coin.mint}:${hasOpenPosition(coin?.position) ? 'open' : 'closed'}`)
    .sort()
    .join('|'),
  () => {
    if (optimisticTargets.value.size === 0) return
    const settled = new Set([...mintList.value, ...coinList.value]
      .filter(coin => hasOpenPosition(coin?.position) || isClosedPosition(coin?.position))
      .map(coin => coin.mint))
    const next = new Set(optimisticTargets.value)
    for (const mint of settled) {
      if (optimisticTimers.has(mint)) { window.clearTimeout(optimisticTimers.get(mint)); optimisticTimers.delete(mint) }
      next.delete(mint)
    }
    optimisticTargets.value = next
  },
)

function handleActionOwn(newOwnerPk) {
  const mint = viewMintId.value
  if (mint && newOwnerPk) store.ownCoin(mint, newOwnerPk).catch(console.error)
}
async function handleCoinModalSubmit(payload) {
  coinModalError.value = ''
  coinModalBusy.value = true
  try {
    const image = (payload.image ?? '').trim() || (coinModalInitial.value?.image ?? '').trim()
    await store.createCoin({
      name: payload.name,
      symbol: payload.symbol,
      description: payload.description || '',
      website: payload.website || '',
      twitter: payload.twitter || '',
      owner: payload.owner,
      image,
      ...(payload.sourceMint ? { source_mint: payload.sourceMint } : {}),
    })
    coinModalVisible.value = false
  } catch (error) {
    coinModalError.value = error?.message || 'coin_create_failed'
  } finally {
    coinModalBusy.value = false
  }
}
function handleActionDelete(entity) {
  const mint = entity?.mint
  if (!mint || !store.coins?.[mint]) return
  if (!window.confirm('Remove this coin from console? On-chain state is not affected.')) return
  store.deleteCoin(mint).catch(console.error)
}
async function handleActionLaunch(payload) {
  const entity = payload?.entity
  const dev = String(devWalletForSelected.value || '').trim()
  if (entity?.mint && dev) await store.launchCoin(entity.mint, dev, String(payload?.amount || '').trim())
}
</script>

<style scoped>
.console-layout{height:100%;min-height:0;padding:8px 8px calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom));background:#0a0a0a;overflow:hidden}.coin-page{height:100%;min-height:0;display:grid;grid-template-rows:auto minmax(0,1fr);gap:8px}.coin-page-head{min-height:46px;padding:8px 10px;border:1px solid #222;border-radius:8px;background:#111;display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px}.coin-title{min-width:0;display:flex;align-items:center;gap:6px;overflow:hidden}.coin-title strong,.coin-title span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.coin-title strong{color:#fff;font-size:14px}.coin-title span{color:#64748b;font-size:11px}.coin-head-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px;min-width:0}.head-pnl{font-weight:900;font-variant-numeric:tabular-nums}.head-pnl.pos{color:#22c55e}.head-pnl.neg{color:#f87171}.head-pnl.flat{color:#93a0b4}.back-btn,.head-target{height:30px;border-radius:999px;border:1px solid #333;background:#141414;color:#e5e7eb;cursor:pointer}.back-btn{width:30px;font-size:20px;line-height:1}.head-target{width:30px;color:#f8fafc;background:#102016;font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif;font-size:18px;font-weight:900}.head-target.active{border-color:#315f42;color:#dcfce7;background:conic-gradient(#22c55e var(--target-progress,0deg),#172117 0),#0b1710}.coin-grid{min-height:0;display:grid;grid-template-columns:minmax(0,1fr);grid-template-rows:minmax(0,1fr);gap:8px;overflow:hidden}.coin-grid>.panel{height:100%;min-height:0}.quick-trade-backdrop,.actions-backdrop{position:fixed;inset:0;z-index:9998;display:flex;align-items:center;justify-content:center;padding:14px;background:rgba(0,0,0,.68)}.quick-trade-modal{width:min(520px,calc(100vw - 28px));max-height:calc(100vh - 28px);overflow-y:auto;border-radius:10px}.actions-modal{width:min(460px,calc(100vw - 28px));max-height:calc(100vh - 80px)}.toast-stack{position:fixed;right:18px;bottom:calc(18px + var(--app-bottom-nav-space,0px));z-index:9000;display:flex;flex-direction:column;gap:8px;pointer-events:none}.toast{width:min(300px,calc(100vw - 32px));border:1px solid #2b2b2b;border-radius:8px;background:#111;padding:10px 12px;box-shadow:0 16px 50px rgba(0,0,0,.4)}.toast strong,.toast span{display:block}.toast strong{color:#fff;font-size:13px}.toast span{margin-top:2px;color:#9ca3af;font-size:12px}.toast.good{border-color:#17603a}.toast.bad{border-color:#6a2424}.toast.exit{border-color:#1d4ed8}@media(max-width:760px){.console-layout{padding:6px 6px calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom))}.coin-page-head{grid-template-columns:32px minmax(0,1fr) auto;gap:7px}.toast-stack{left:12px;right:12px;bottom:64px}.toast{width:auto}}
</style>
